import { openMessagePopup } from './utils/PopupHelper';

type AuthenticationInfo = {
  authenticated: boolean | undefined;
  guestEnabled: boolean;
};
const authCache = new Map<string, AuthenticationInfo>();

export type CookieAuthenticationCredentialConfiguration = {
  url: string;
  guest?: boolean;
};

async function performGuestLogon(server: string) {
  const reportsResp = await fetch(`${server}/reports/?sso_guest=true`, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'include',
  });
  if (!reportsResp.ok) {
    throw new Error('invalid logon response');
  }
}

export class CookieAuthenticationCredential {
  private _config: CookieAuthenticationCredentialConfiguration;
  private _server: string;
  constructor(config: CookieAuthenticationCredentialConfiguration) {
    this._config = config;
    this._server = config.url;
    if (config.guest) {
      authCache.set(this._config.url, {
        ...this._getAuthenticationInfo(),
        guestEnabled: true,
      });
    }
  }

  private _getAuthenticationInfo(): AuthenticationInfo {
    return (
      authCache.get(this._config.url) ?? {
        authenticated: undefined,
        guestEnabled: false,
      }
    );
  }

  private _setStoreStatus(authenticated: boolean | undefined) {
    const authenticationInfo = this._getAuthenticationInfo();

    authCache.set(this._config.url, {
      ...authenticationInfo,
      authenticated,
    });
  }

  /**
   * Checks the server endpoint to see if the user is authenticated. If there is not user session
   * and any config enabled guest, then a guest session will be created. Otherwise false will be
   * returned.
   * @returns true if authenticated
   */
  private async _checkEndpoint(): Promise<boolean> {
    try {
      const resp = await fetch(`${this._server}/SASVisualAnalytics/logon/index.html`, {
        credentials: 'include',
      });
      const text = await resp.text();
      if (text.includes('data-report-sas-auth-success')) {
        this._setStoreStatus(true);
        return true;
      }
      throw new Error('Not authenticated.');
    } catch {
      if (this._getAuthenticationInfo().guestEnabled) {
        const status = await performGuestLogon(this._server).then(
          () => true,
          () => false
        );
        this._setStoreStatus(status);
        return status;
      }
    }

    this._setStoreStatus(false);
    return false;
  }

  /**
   * Checks to see if any user is authenticated.
   *
   * @rejects: If no user is authenticated.
   */
  async checkAuthenticated(): Promise<void> {
    const status = this._getAuthenticationInfo().authenticated ?? (await this._checkEndpoint());
    if (!status) {
      throw `Not authenticated on ${this._server}`;
    }
  }

  /**
   * Launches a popup window that navigates to the authentication endpoint and allows the user to login.
   *
   * @rejects: When login fails (for example: user closes the popup).
   */
  async loginPopup(): Promise<void> {
    return new Promise(async (accept, reject) => {
      try {
        await openMessagePopup(`${this._server}/SASVisualAnalytics/logon/index.html`, '');
        accept();
      } catch (e) {
        reject(`${this._server} failed to log in`);
      }
    });
  }

  /**
   * Makes endpoint call to end the current cookie session.
   *
   * @rejects: When logout fails.
   */
  async logout(): Promise<void> {
    const res = await fetch(`${this._server}/SASLogon/logout.do`, {
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!res.ok) {
      throw new Error('Failed to logout.');
    }
  }

  /**
   * If `checkAuthenticated` has already been called, a cached value may be returned the next time.
   * This is done to reduce the number of potential service calls needed when making multiple API
   * calls. Calling `invalidateCache` will force `checkAuthenticated` to re-validate the next time
   * it is called. **This is not needed for most use cases.**
   */
  invalidateCache() {
    this._setStoreStatus(undefined);
  }
}
